import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ArrowRight, Loader2 } from "lucide-react";
import { executeRecaptcha } from "../lib/recaptcha";
import RecaptchaNotice from "./RecaptchaNotice";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadFormHero() {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    property_address: "",
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.property_address) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (!consent) {
      toast.error("Please agree to be contacted before submitting.");
      return;
    }
    setLoading(true);
    try {
      const { token, action } = await executeRecaptcha("lead_hero");
      await axios.post(`${API}/leads`, {
        ...form,
        source: "hero",
        consent: true,
        recaptcha_token: token,
        recaptcha_action: action,
      });
      trackEvent("lead_submit", { form: "hero" });
      trackEvent("generate_lead", { form: "hero", currency: "USD", value: 1 });
      setDone(true);
      toast.success("Offer request received. We'll call you within 24 hours.");
      setForm({ full_name: "", phone: "", email: "", property_address: "" });
      setConsent(false);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please call (910) 212-5006.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      id="lead"
      data-testid="hero-lead-form-card"
      className="rounded-2xl border border-[var(--c4p-line)] bg-white p-7 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.45)]"
    >
      <h3 className="font-display text-[28px] leading-none text-[var(--c4p-forest)]">
        Get your cash offer
      </h3>
      <p className="mt-2 font-sans-ui text-[13.5px] text-[var(--c4p-muted)]">
        Takes 60 seconds. Zero obligation.
      </p>

      {done ? (
        <div
          data-testid="hero-form-success"
          className="mt-6 rounded-xl border border-[var(--c4p-orange)]/40 bg-[var(--c4p-orange)]/5 p-5 text-center"
        >
          <div className="font-display text-[22px] text-[var(--c4p-forest)]">
            Thanks — we got it.
          </div>
          <p className="mt-2 font-sans-ui text-sm text-[var(--c4p-muted)]">
            A real person will call you within 24 hours at the number you
            provided.
          </p>
          <button
            onClick={() => setDone(false)}
            data-testid="hero-form-reset-btn"
            className="mt-4 font-sans-ui text-[12px] font-semibold uppercase tracking-widest text-[var(--c4p-orange)] underline"
          >
            Submit another property
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Full Name"
              name="hero-name"
              value={form.full_name}
              onChange={(v) => update("full_name", v)}
              placeholder="Jane Doe"
            />
            <Field
              label="Phone"
              name="hero-phone"
              value={form.phone}
              onChange={(v) => update("phone", v)}
              placeholder="(555) 555-0100"
              type="tel"
            />
          </div>
          <Field
            label="Email"
            name="hero-email"
            value={form.email}
            onChange={(v) => update("email", v)}
            placeholder="jane@example.com"
            type="email"
          />
          <Field
            label="Property Address"
            name="hero-address"
            value={form.property_address}
            onChange={(v) => update("property_address", v)}
            placeholder="123 Main St, Greenville, NC"
          />

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-[var(--c4p-line)] bg-[var(--c4p-cream)]/50 p-3">
            <input
              type="checkbox"
              data-testid="hero-consent-checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--c4p-orange)]"
            />
            <span className="font-sans-ui text-[12px] leading-snug text-neutral-700">
              I agree to be contacted by Cash 4 Properties at the phone and
              email above, including by call, SMS, or automated messages.
              Consent is not required to receive an offer. Message/data rates
              may apply. See our{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Terms
              </a>
              .
            </span>
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid="hero-submit-btn"
            className="btn-primary w-full disabled:opacity-70"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={16} /> Sending…
              </>
            ) : (
              <>
                Get My Cash Offer <ArrowRight size={16} />
              </>
            )}
          </button>

          <div className="flex items-center justify-center gap-2 pt-1 font-sans-ui text-[11.5px] text-[var(--c4p-muted)]">
            <Lock size={12} /> Your info is private. We never share or spam.
          </div>
          <RecaptchaNotice className="text-center" />
        </form>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", name }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-sans-ui text-[12.5px] font-semibold text-[var(--c4p-ink)]">
        {label}
      </span>
      <input
        data-testid={`field-${name}`}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="input-ui"
      />
    </label>
  );
}
